import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import {
  OpenFeature,
  useBooleanFlagValue,
  useObjectFlagValue,
} from '@openfeature/react-sdk';

/**
 * Umbrella kill switch for the ownership-aware self-healing session/recording
 * stop (desktop, online-mic transport only).
 *
 * Gates the entire feature: the unified native terminal routine, the epoch /
 * tombstone ownership model, the autonomous watchdog, the FE lease/heartbeat,
 * and the durable encrypted-MP3 backup recovery. Default OFF — with the flag
 * OFF the app behaves identically to today (legacy stop path, no watchdog, no
 * boot reconcile). Read once and locked at session start so a mid-recording
 * flip can never half-arm a live recording.
 *
 * The flag is payload-carrying: its value supplies the runaway/eviction
 * thresholds, decoded on the Rust side with safe fallbacks. This is a
 * rollout/kill gate, not an experiment — do not add `useManualExposure`.
 *
 * Lives in this standalone `.ts` module (not the `index.tsx` barrel) so the hook
 * does not add a `react-refresh/only-export-components` warning to the component
 * barrel. Import it directly from `@/features/flags/self-healing-stop-flag`.
 */
export const SELF_HEALING_STOP_FLAG_KEY = 'release-apps-self-healing-stop-desktop';

/** Safe fallback (FR-022a/FR-032): any genuine failed chunk is unhealthy. */
export const DEFAULT_FAILED_CHUNK_THRESHOLD = 0;

/**
 * Safe fallback for `feLeaseGraceSecs` — mirrors the Rust
 * `DEFAULT_FE_LEASE_GRACE` (`recording_lifecycle/config.rs`). The grace must
 * exceed a normal webview reload, so it is never configured shorter than a few
 * seconds.
 */
export const DEFAULT_FE_LEASE_GRACE_SECS = 60;

/**
 * Floor for the FE lease grace, in seconds. Below this a heartbeat cannot fit
 * inside the window: `useRecordingLease` never polls faster than its
 * `MIN_HEARTBEAT_INTERVAL_MS` (5s) and wants `HEARTBEATS_PER_GRACE` (3) beats
 * per window, so 5s x 3 = 15s is the shortest grace a healthy webview can
 * actually satisfy. A shorter one inverts the safety property this signal
 * exists to serve — every healthy webview would read stale at every
 * evaluation, and a watchdog built on that would force-terminate healthy
 * recordings. Clamped at the source so an ops typo can never configure it.
 */
export const MIN_FE_LEASE_GRACE_SECS = 15;

export const useIsSelfHealingStopEnabled = () => {
  return useBooleanFlagValue(SELF_HEALING_STOP_FLAG_KEY, false);
};

/**
 * Imperative (non-hook) read of the same kill switch, for use inside
 * `mutationFn`/callbacks that cannot call a React hook conditionally deep in
 * an async flow. Mirrors the door's existing imperative flag reads (e.g.
 * `isGenerateConsultNoteFromDocumentTemplateEnabled`,
 * `src/features/flags/index.tsx`): `OpenFeature.getClient(...)` resolves
 * against the default NoOp evaluation outside a `<OpenFeatureProvider>`, so —
 * unlike `useBooleanFlagValue` — this is safe to call with no provider
 * mounted (e.g. every existing stop-mutation unit test).
 */
export const isSelfHealingStopEnabled = (): boolean => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    SELF_HEALING_STOP_FLAG_KEY,
    false
  );
};

/**
 * Imperative read of the kill-switch flag's raw Flagsmith payload, for the
 * arm path that plumbs it down to Rust (`online_backup_arm_reconcile`), where
 * it is decoded into the self-healing thresholds — including the T067
 * `asyncHealthyBackup` rollout toggle — with fail-closed fallbacks.
 * `undefined` while the flag is off (the payload is meaningless then) or when
 * the payload is not a JSON object.
 */
export const getSelfHealingStopPayload = (): Record<string, unknown> | undefined => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  if (!client.getBooleanValue(SELF_HEALING_STOP_FLAG_KEY, false)) {
    return undefined;
  }
  const payload = client.getObjectValue(SELF_HEALING_STOP_FLAG_KEY, {});
  return typeof payload === 'object' && payload !== null && !Array.isArray(payload)
    ? (payload as Record<string, unknown>)
    : undefined;
};

/**
 * Extracts the failed-chunk threshold (FR-022a) from the flag's Flagsmith
 * payload, falling back to {@link DEFAULT_FAILED_CHUNK_THRESHOLD}. `0` is a
 * valid, meaningful value (web intent: any genuine failed chunk is
 * unhealthy), so it must be distinguished from "missing"/malformed rather
 * than treated as falsy.
 */
export const parseFailedChunkThreshold = (payload: unknown): number => {
  const threshold = (payload as { failedChunkThreshold?: unknown } | null)
    ?.failedChunkThreshold;
  return typeof threshold === 'number' && Number.isFinite(threshold) && threshold >= 0
    ? Math.floor(threshold)
    : DEFAULT_FAILED_CHUNK_THRESHOLD;
};

/**
 * Extracts the FE lease grace (FR-015) from the flag's Flagsmith payload,
 * falling back to {@link DEFAULT_FE_LEASE_GRACE_SECS}. This is the window the
 * native watchdog gives a silent webview before treating the recording as
 * orphaned, so the FE heartbeat interval is derived from it rather than
 * hardcoded — an ops change that shortens the grace must shorten the interval
 * with it, or every healthy recording starts looking orphaned.
 *
 * A non-positive or non-finite value is meaningless as a grace and falls back.
 * A positive but unusably short one is clamped up to
 * {@link MIN_FE_LEASE_GRACE_SECS} rather than honoured: the heartbeat floor
 * downstream cannot beat fast enough to satisfy it, so honouring it would make
 * every healthy webview look orphaned.
 */
export const parseFeLeaseGraceSecs = (payload: unknown): number => {
  const grace = (payload as { feLeaseGraceSecs?: unknown } | null)?.feLeaseGraceSecs;
  return typeof grace === 'number' && Number.isFinite(grace) && grace > 0
    ? Math.max(MIN_FE_LEASE_GRACE_SECS, Math.floor(grace))
    : DEFAULT_FE_LEASE_GRACE_SECS;
};

/**
 * The configured FE lease grace, gated on the same kill switch. A **hook**, not
 * the imperative read its `getFailedChunkThreshold` sibling uses, because the
 * grace has to be honoured *live*: it re-renders on a Flagsmith change so
 * `useRecordingLease` re-derives its heartbeat interval the moment ops shortens
 * the window. An imperative read is latched at whatever the value was when the
 * effect mounted, which keeps the FE beating on the old, longer interval
 * against a newly-shortened grace — i.e. looking orphaned while it is healthy.
 *
 * Mirrors {@link useDesktopChunkUploadConcurrency} in
 * `flags/chunk-upload-concurrency.ts`: `getObjectValue` returns the payload
 * even with the flag toggled off, so the enabled state gates the read.
 */
export const useFeLeaseGraceSecs = (): number => {
  const enabled = useBooleanFlagValue(SELF_HEALING_STOP_FLAG_KEY, false);
  const payload = useObjectFlagValue(SELF_HEALING_STOP_FLAG_KEY, {});
  return enabled ? parseFeLeaseGraceSecs(payload) : DEFAULT_FE_LEASE_GRACE_SECS;
};

/**
 * Imperative failed-chunk threshold, gated on the same kill switch (the
 * payload is meaningless while the feature is off). See
 * {@link isSelfHealingStopEnabled} for why this is a plain function rather
 * than a hook.
 */
export const getFailedChunkThreshold = (): number => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  if (!client.getBooleanValue(SELF_HEALING_STOP_FLAG_KEY, false)) {
    return DEFAULT_FAILED_CHUNK_THRESHOLD;
  }
  return parseFailedChunkThreshold(client.getObjectValue(SELF_HEALING_STOP_FLAG_KEY, {}));
};
